package org.dronda.api.wishlist

import com.github.michaelbull.result.Err
import com.github.michaelbull.result.Ok
import com.github.michaelbull.result.Result
import com.github.michaelbull.result.andThen
import com.github.michaelbull.result.map
import com.github.michaelbull.result.onErr
import com.github.michaelbull.result.toResultOr
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable
import org.dronda.lib.validation.ValidationError
import org.dronda.lib.validation.collect
import org.dronda.lib.validation.rules.date
import org.dronda.lib.validation.rules.email
import org.dronda.lib.validation.rules.maxLength
import org.dronda.lib.validation.rules.minLength
import org.dronda.lib.validation.rules.regex
import org.dronda.lib.validation.rules.url
import org.dronda.lib.validation.validate

typealias ValidationRule<T, R> = (T) -> Result<R, ValidationError>

@Serializable
data class EventRequest(
    val name: String,
    val date: LocalDate,
    val groupId: String? = null,
    val mode: EventMode = EventMode.STANDARD,
    val budget: String? = null,
    val participantEmails: List<String> = emptyList(),
    val exclusions: List<ExclusionEmails> = emptyList(),
) {
    companion object {
        val nameValidation: (name: String) -> Result<String, ValidationError> = { name ->
            validate("event.name", maxLength(name, 100).andThen { minLength(it, 1) })
        }
        val dateValidation: (date: String) -> Result<String, ValidationError> = { date ->
            validate("event.date", date(date))
        }
        val groupIdValidation: (groupId: String) -> Result<String, ValidationError> = { id ->
            validate("event.groupId", maxLength(id, 50))
        }
        val budgetValidation: ValidationRule<String, String> = { budget ->
            validate("event.budget", maxLength(budget, 50))
        }
        val participantEmailValidation: ValidationRule<String, String> = { email ->
            validate("event.participantEmail", maxLength(email, 150).andThen { email(it) }.map { it.value })
        }
        val participantsValidation: ValidationRule<Participants, Participants> = { participants ->
            validate(
                "event.participants",
                maxSize(participants.emails, EventLimits.MAX_PARTICIPANTS - 1)
                    .andThen { emails -> firstError(emails, participantEmailValidation) }
                    .andThen { maxSize(participants.exclusions, MAX_EXCLUSIONS) }
                    .andThen { exclusions -> firstError(exclusions, exclusionValidation) }
                    .map { participants }
            )
        }
        val exclusionValidation: ValidationRule<ExclusionEmails, ExclusionEmails> = { exclusion ->
            if (exclusion.a.trim().equals(exclusion.b.trim(), ignoreCase = true)) {
                Err(SelfExclusion(exclusion.a))
            } else {
                Ok(exclusion)
            }
        }

        fun from(
            name: String,
            date: String,
            groupId: String?,
            mode: EventMode = EventMode.STANDARD,
            budget: String? = null,
            participantEmails: List<String> = emptyList(),
            exclusions: List<ExclusionEmails> = emptyList(),
        ) : Result<EventRequest, List<ValidationError>> = collect(
            nameValidation(name),
            dateValidation(date),
            groupId?.let { groupIdValidation(it) } ?: Ok(null),
            budget?.takeIf { it.isNotBlank() }?.let { budgetValidation(it) } ?: Ok(null),
            participantsValidation(
                Participants(
                    emails = participantEmails.map { it.trim() }.filter { it.isNotEmpty() }.distinct(),
                    exclusions = exclusions.map { it.copy(a = it.a.trim(), b = it.b.trim()) },
                )
            )
        ) { validName, validDate, valueGroup, validBudget, validParticipants ->
            EventRequest(
                name = validName,
                date = LocalDate.parse(validDate),
                groupId = valueGroup,
                mode = mode,
                budget = validBudget,
                participantEmails = validParticipants.emails,
                exclusions = validParticipants.exclusions,
            )
        }

        private const val MAX_EXCLUSIONS = 200
    }

    data class Participants(
        val emails: List<String>,
        val exclusions: List<ExclusionEmails>,
    )
}

@Serializable
data class ExclusionEmails(
    val a: String,
    val b: String,
    val direction: ExclusionDirection = ExclusionDirection.BOTH,
)

@Serializable
data class UpdateEventRequest(
    val name: String,
    val date: LocalDate,
    val mode: EventMode,
    val budget: String?,
) {
    companion object {
        fun from(
            name: String,
            date: String,
            mode: EventMode,
            budget: String?,
        ): Result<UpdateEventRequest, List<ValidationError>> = collect(
            EventRequest.nameValidation(name),
            EventRequest.dateValidation(date),
            budget?.takeIf { it.isNotBlank() }?.let { EventRequest.budgetValidation(it) } ?: Ok(null),
        ) { validName, validDate, validBudget ->
            UpdateEventRequest(
                name = validName,
                date = LocalDate.parse(validDate),
                mode = mode,
                budget = validBudget,
            )
        }
    }
}

@Serializable
data class UpdateExclusionsRequest(
    val exclusions: List<ExclusionPair>,
)

@Serializable
data class SendAssignmentsRequest(
    val kind: SendKind,
)

data class TooManyEntries(val max: Int) : ValidationError()
data class SelfExclusion(val email: String) : ValidationError()

private fun <T> maxSize(values: List<T>, max: Int): Result<List<T>, ValidationError> =
    if (values.size > max) Err(TooManyEntries(max)) else Ok(values)

private fun <T, R> firstError(values: List<T>, rule: ValidationRule<T, R>): Result<List<T>, ValidationError> {
    values.forEach { value -> rule(value).onErr { return Err(it) } }
    return Ok(values)
}

@Serializable
data class CreateWishList(
    val name: String,
    val eventId: String,
) {
    companion object {
        val nameValidation: (name: String) -> Result<String, ValidationError> = { name ->
            validate("wishlist.name", maxLength(name, 100))
        }
        val eventIdValidation: (eventId: String) -> Result<String, ValidationError> = { eventId ->
            validate("wishlist.eventId", maxLength(eventId, 36))
        }

        fun from(
            name: String,
            eventId: String,
        ): Result<CreateWishList, List<ValidationError>> = collect(
            nameValidation(name),
            eventIdValidation(eventId),
            ::CreateWishList
        )
    }
}

@Serializable
data class AddWishlistItem(
    val name: String,
    val description: String?,
    val url: String?,
) {
    companion object {
        val nameValidation: ValidationRule<String, String> = { name ->
            validate("wishlist.item.name", maxLength(name, 100).andThen { minLength(it, 1) })
        }
        val descriptionValidation: ValidationRule<String, String> = { description ->
            validate("wishlist.item.description", maxLength(description, 200).andThen { minLength(it, 1) })
        }
        val urlValidation: ValidationRule<String, String> = { url ->
            validate("wishlist.item.url", maxLength(url, 500).andThen { minLength(it, 5) }.andThen { url(it) })
        }

        fun from(
            name: String,
            description: String?,
            url: String?,
        ) = collect(
            a0 = nameValidation(name),
            a1 = description?.let { descriptionValidation(it) } ?: Ok(null),
            a2 = url?.let { urlValidation(it) } ?: Ok(null),
            collector = ::AddWishlistItem
        )
    }
}

@Serializable
data class EditWishlistItem(
    val name: String,
    val description: String?,
    val url: String?,
) {
    companion object {
        val nameValidation: ValidationRule<String, String> = { name ->
            validate("wishlist.item.name", maxLength(name, 100).andThen { minLength(it, 1) })
        }
        val descriptionValidation: ValidationRule<String, String> = { description ->
            validate("wishlist.item.description", maxLength(description, 200).andThen { minLength(it, 1) })
        }
        val urlValidation: ValidationRule<String, String> = { url ->
            validate("wishlist.item.url", maxLength(url, 500).andThen { minLength(it, 5) }.andThen { url(it) })
        }

        fun from(
            name: String,
            description: String?,
            url: String?,
        ) = collect(
            a0 = nameValidation(name),
            a1 = description?.let { descriptionValidation(it) } ?: Ok(null),
            a2 = url?.let { urlValidation(it) } ?: Ok(null),
            collector = ::EditWishlistItem
        )
    }
}

@Serializable
data class AddParticipantRequest(
    val email: String
)
